import { useState } from 'react'
import baseUrl from '../../api/baseUrl'
import getDecryptedToken from '../../api/getDecryptedToken'
import type { LabOrderDetail } from './labOrderTypes'

type ClinicOrderStagePanelProps = {
  order: LabOrderDetail
  onUpdated: (order: LabOrderDetail) => void
}

const EXCEPTION_STAGES = ['On Hold', 'Cancelled']

/**
 * Logs a stage on a lab order from the clinic. The server decides which
 * stages the clinic may log (order.allowedStages): any stage for a lab the
 * clinic tracks itself; only Try-in and Delivered once a registered lab has
 * accepted; only Cancel while the lab has not accepted yet.
 */
export default function ClinicOrderStagePanel({
  order,
  onUpdated,
}: ClinicOrderStagePanelProps) {
  const allowed = order.allowedStages ?? []
  const awaiting = !!order.awaitingAcceptance
  const [stage, setStage] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [cancelling, setCancelling] = useState(false)

  const currentIdx = order.workflow.findIndex((s) => s.state === 'current')
  const nextStep = order.workflow[currentIdx + 1]?.title
  const defaultStage =
    nextStep && allowed.includes(nextStep) ? nextStep : allowed[0] || ''
  const picked = awaiting ? 'Cancelled' : stage || defaultStage
  const notesRequired = EXCEPTION_STAGES.includes(picked)

  if (!allowed.length) return null

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!picked) return
    if (notesRequired && !notes.trim()) {
      setError(
        picked === 'Cancelled'
          ? 'Add a note to say why the order is cancelled'
          : 'Add a note to say why the order is on hold',
      )
      return
    }
    setSaving(true)
    setError('')
    try {
      const res = await baseUrl.post(
        `client/labs/orders/${encodeURIComponent(order._id)}/stages`,
        {
          stage: picked,
          notes: notes.trim(),
          addedBy: getDecryptedToken('userName') || 'Clinic',
        },
      )
      onUpdated(res.data.data)
      setStage('')
      setNotes('')
      setCancelling(false)
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to save. Try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  const stepState = (title: string) =>
    order.workflow.find((s) => s.title === title)?.state

  if (awaiting) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-950/30">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Waiting for {order.labName} to accept
            </p>
            <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
              The lab tracks the stages once it accepts. You can still cancel
              the order until then.
            </p>
          </div>
          {!cancelling ? (
            <button
              type="button"
              onClick={() => setCancelling(true)}
              className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-slate-900 dark:text-red-300"
            >
              Cancel order
            </button>
          ) : null}
        </div>
        {cancelling ? (
          <form onSubmit={submit} className="mt-3 space-y-2">
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason, e.g. patient changed the treatment plan"
              className="w-full rounded-lg border bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              disabled={saving}
            />
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCancelling(false)
                  setError('')
                }}
                disabled={saving}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-white/60"
              >
                Keep order
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Cancelling…' : 'Cancel order'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    )
  }

  const workflowOptions = allowed.filter((s) => !EXCEPTION_STAGES.includes(s))
  const exceptionOptions = allowed.filter((s) => EXCEPTION_STAGES.includes(s))
  const isLocal = order.isLocalTracking || order.trackingMode === 'local'

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-primary/20 bg-primary/5 p-4 dark:border-primary/30 dark:bg-primary/10"
    >
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        Add stage
      </h3>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
        {isLocal
          ? 'Pick where the order is now. Earlier stages are marked done.'
          : `${order.labName} updates the other stages. Log the try-in or delivery when it happens at your clinic.`}
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-start">
        <select
          value={picked}
          onChange={(e) => {
            setStage(e.target.value)
            setError('')
          }}
          className="w-full rounded-lg border bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
          disabled={saving}
          aria-label="Stage"
        >
          {workflowOptions.map((s) => (
            <option key={s} value={s}>
              {s}
              {stepState(s) === 'current'
                ? ' (current)'
                : stepState(s) === 'completed'
                  ? ' (done)'
                  : ''}
            </option>
          ))}
          {exceptionOptions.length ? (
            <optgroup label="Exceptions">
              {exceptionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={notesRequired ? 'Reason (required)' : 'Notes (optional)'}
          className="w-full rounded-lg border bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
          disabled={saving}
          aria-label="Notes"
        />
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add stage'}
        </button>
      </div>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </form>
  )
}
